import { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useSubmitContactFormMutation } from "../api/contactFormApiSlice";

const initialValues = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  company_name: "",
  message: "",
};

const validationSchema = Yup.object().shape({
  first_name: Yup.string().required("First name is required"),
  last_name: Yup.string().required("Second name is required"),
  email: Yup.string().email("Invalid email").required("Email is required"),
  phone: Yup.string().matches(
    /^\s*(([+]\s?\d[-\s]?\d|0)?\s?\d([-\s]?\d){9}|[(]\s?\d([-\s]?\d)+\s*[)]([-\s]?\d)+)\s*$/,
    "Invalid phone number"
  ),
  message: Yup.string().required("Message is required"),
});

function useContactForm() {
  const [submitContactForm, { isLoading, isError }] =
    useSubmitContactFormMutation();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");

  const { executeRecaptcha } = useGoogleReCaptcha();

  const onSubmit = async (values, { setStatus, resetForm, setFieldError }) => {
    setIsSubmitting(true);

    try {
      if (!executeRecaptcha) {
        setErrorMessage("reCAPTCHA not available. Please refresh the page.");
        setIsSubmitting(false);
        return;
      }

      const recaptchaToken = await executeRecaptcha("contact_form_submission");

      const submissionData = {
        ...values,
        recaptcha: recaptchaToken,
      };

      const response = await submitContactForm(submissionData).unwrap();

      setStatus({ success: true });
      setSuccessMessage(response.success);
      setErrorMessage("");
      resetForm();
    } catch (error) {
      setStatus({ success: false });

      if (error?.data?.error) {
        setErrorMessage(error.data.error); // string
        setSuccessMessage("");
      } else if (error?.data && typeof error.data === 'object') {
        if (Array.isArray(error.data)) {
          setErrorMessage(error.data[0] || "An unexpected error occurred");
        } else {
          Object.keys(error.data).forEach((field) => {
            const message = Array.isArray(error.data[field])
              ? error.data[field][0]
              : error.data[field];
            setFieldError(field, message);
          });
        }
      } else {
        setErrorMessage("An unexpected error occurred. Please try again later.");
        setSuccessMessage("");
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactForm = useFormik({
    initialValues,
    onSubmit,
    validationSchema,
  });

  return [contactForm, isLoading, isSubmitting, isError, successMessage, errorMessage];
}

export default useContactForm;
